/**
 * UR Brand System — Phase 5b admin workspace.
 *
 * Single screen, tabbed. Each tab maps to a Phase 5a sub-workflow:
 *   OVERVIEW  — status, hold/resume/cancel, strategy change
 *   LIBRARY   — Visual Library browser/curator
 *   TERRITORY — reviewer territory assembly, QA, ready, send
 *   CONCEPTS  — Gate 1 workspace, send concepts to customer
 *   FEEDBACK  — customer feedback classification (refinement vs direction change)
 *   IDENTITY  — Identity Handoff builder + approve
 *   BLUEPRINT — EN + ES authoring
 *   PACKAGE   — Package Manifest + Gate 2 + Delivery
 *
 * English only (internal). No numeric scoring. Separation-of-duties
 * enforced by the backend; the UI surfaces 409 messages verbatim.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, GATE1_CRITERIA, GATE2_CRITERIA, overallFrom } from "@/lib/brandApi";

const TABS = [
  ["overview", "Overview"], ["library", "Visual Library"],
  ["territory", "Territories"], ["concepts", "Gate 1 / Concepts"],
  ["feedback", "Feedback"], ["identity", "Identity Handoff"],
  ["blueprint", "Blueprint"], ["package", "Package / Gate 2"],
];

export default function BrandPhase5Workspace() {
  const { projectId } = useParams();
  const api = useMemo(() => adminApi(), []);
  const [tab, setTab] = useState("overview");
  const [project, setProject] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/admin/brand-projects/${projectId}`);
      setProject(r.data); setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "load failed");
    }
  }, [api, projectId]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!project) return <div className="min-h-screen bg-black text-neutral-400 p-10">{err || "Loading…"}</div>;

  const biz = ((project.brand_data?.business || {}).business_name || {}).value || "(no name)";

  return (
    <div className="min-h-screen bg-black text-neutral-100">
      <header className="border-b border-neutral-800 px-6 py-4 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="flex items-center gap-3">
          <div className="font-black text-orange-500">Ur Designs</div>
          <div className="text-xs text-neutral-500">Phase 5 Workspace</div>
          <div className="ml-6 text-sm font-black">{biz}</div>
          <div className="ml-auto flex items-center gap-2">
            <span data-testid="project-status-pill"
              className="px-2 py-1 text-[11px] tracking-widest border border-neutral-700 rounded-full">
              {project.project_status}
            </span>
          </div>
        </div>
        <nav className="flex gap-1 mt-4 overflow-x-auto" data-testid="phase5-tabs">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-3 py-2 text-xs tracking-widest uppercase rounded-md whitespace-nowrap ${
                tab === k ? "bg-orange-500 text-black font-bold"
                          : "text-neutral-400 hover:text-neutral-200"
              }`}>{label}</button>
          ))}
        </nav>
      </header>

      {err && <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3 m-4 rounded">
        {err}</div>}

      <main className="p-6">
        {tab === "overview" && <OverviewTab project={project} refresh={refresh} />}
        {tab === "library" && <LibraryTab />}
        {tab === "territory" && <TerritoryTab project={project} refresh={refresh} />}
        {tab === "concepts" && <ConceptsTab project={project} refresh={refresh} />}
        {tab === "feedback" && <FeedbackTab project={project} refresh={refresh} />}
        {tab === "identity" && <IdentityTab project={project} refresh={refresh} />}
        {tab === "blueprint" && <BlueprintTab project={project} />}
        {tab === "package" && <PackageTab project={project} refresh={refresh} />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function OverviewTab({ project, refresh }) {
  const api = adminApi();
  const [reason, setReason] = useState("");
  const [changed, setChanged] = useState([]);
  const sections = [
    "business_snapshot", "audience_interpretation", "competitive_context",
    "positioning_value_proposition", "brand_character_messaging", "creative_direction",
  ];
  const toggle = (s) => setChanged(changed.includes(s)
    ? changed.filter((x) => x !== s) : [...changed, s]);

  const call = async (label, fn) => {
    try { await fn(); await refresh(); toast.success(label); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="p-4 border border-neutral-800 rounded-lg">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">PROJECT CONTROLS</div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="btn-hold"
            onClick={() => call("ON_HOLD", () => api.post(
              `/admin/brand-projects/${project.project_id}/hold`, { reason }))}
            className="px-3 py-2 border border-neutral-700 rounded text-sm">Hold</button>
          <button data-testid="btn-resume"
            onClick={() => call("Resumed", () => api.post(
              `/admin/brand-projects/${project.project_id}/resume`))}
            className="px-3 py-2 border border-neutral-700 rounded text-sm">Resume</button>
          <button data-testid="btn-cancel"
            onClick={() => {
              if (!window.confirm("Cancel this project? This is deliberate — the project cannot continue.")) return;
              call("Cancelled", () => api.post(
                `/admin/brand-projects/${project.project_id}/cancel`, { reason }));
            }}
            className="px-3 py-2 border border-red-500/40 text-red-300 rounded text-sm">Cancel…</button>
        </div>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional)"
          className="mt-3 w-full bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm" />
        {project.on_hold_prior_status && (
          <div className="mt-3 text-xs text-neutral-500">
            Will resume to: <span className="text-neutral-300">{project.on_hold_prior_status}</span>
          </div>
        )}
      </div>

      <div className="p-4 border border-neutral-800 rounded-lg" data-testid="strategy-change-panel">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">STRATEGY CHANGE DECLARATION</div>
        <p className="text-xs text-neutral-400 mb-3">
          Declare material strategy changes. Visual-bearing sections invalidate
          downstream territory/direction/brief/concepts. Messaging-only changes do not.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {sections.map((s) => (
            <label key={s} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={changed.includes(s)} onChange={() => toggle(s)}
                data-testid={`sect-${s}`} />
              <span>{s.replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
        <button data-testid="btn-strategy-change"
          disabled={!changed.length}
          onClick={() => call("Change recorded", () => api.post(
            `/admin/brand-projects/${project.project_id}/strategy-change`,
            { changed_sections: changed }))}
          className="px-3 py-2 bg-orange-500 text-black rounded text-sm font-bold disabled:opacity-30">
          Record strategy change
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function LibraryTab() {
  const api = adminApi();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ category: "typography", title: "", image_url: "", rights_status: "inspiration_only", tags: "" });
  const load = async () => {
    try { const r = await api.get("/admin/brand-visual-library"); setItems(r.data || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      await api.post("/admin/brand-visual-library", {
        ...draft, tags: draft.tags.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  return (
    <div className="max-w-5xl space-y-6">
      <div className="p-4 border border-neutral-800 rounded-lg grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="vlib-add">
        <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
          className="bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" data-testid="vlib-category">
          {["typography", "color_direction", "photography_style", "illustration_style",
            "composition_feel", "texture_material", "logo_reference", "mood_atmosphere",
            "layout_system", "other"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Title" className="bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" data-testid="vlib-title" />
        <input value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
          placeholder="Image URL" className="bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" data-testid="vlib-image-url" />
        <select value={draft.rights_status} onChange={(e) => setDraft({ ...draft, rights_status: e.target.value })}
          className="bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" data-testid="vlib-rights">
          <option value="inspiration_only">inspiration_only</option>
          <option value="licensed_for_delivery">licensed_for_delivery</option>
          <option value="owned_by_ur_designs">owned_by_ur_designs</option>
          <option value="ai_generated">ai_generated (classification only)</option>
        </select>
        <button onClick={add} className="bg-orange-500 text-black font-bold rounded px-3 text-sm" data-testid="vlib-add-btn">Add</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="vlib-grid">
        {items.map((v) => (
          <div key={v.visual_id} className="border border-neutral-800 rounded-lg overflow-hidden" data-testid={`vlib-${v.visual_id}`}>
            {v.image_url ? <img src={v.image_url} alt="" className="aspect-square object-cover" />
                         : <div className="aspect-square bg-neutral-900" />}
            <div className="p-2 text-xs">
              <div className="truncate font-bold">{v.title || v.visual_id}</div>
              <div className="text-neutral-500 truncate">{v.category}</div>
              <div className="text-[10px] text-neutral-500 mt-1">{v.rights_status}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function TerritoryTab({ project, refresh }) {
  const api = adminApi();
  const [tsets, setTsets] = useState([]);
  const [visuals, setVisuals] = useState([]);
  const [draft, setDraft] = useState({ title: "", rationale: "", visuals: [] });
  const [render, setRender] = useState({ language: "en", headline: "", description: "" });
  const [sendLangs, setSendLangs] = useState(["en"]);
  const [previewMode, setPreviewMode] = useState(false);

  const load = async () => {
    try {
      const r1 = await api.get(`/admin/brand-projects/${project.project_id}/territory-sets`).catch(() => null);
      if (r1) setTsets(Array.isArray(r1.data) ? r1.data : [r1.data]);
    } catch {}
    try { const v = await api.get("/admin/brand-visual-library"); setVisuals(v.data || []); } catch {}
  };
  // The admin list endpoint returns a single preparation set; fall back to raw.
  useEffect(() => { (async () => {
    try {
      const r = await api.post(`/admin/brand-projects/${project.project_id}/territory-sets`);
      setTsets([r.data]);
    } catch {}
    try { const v = await api.get("/admin/brand-visual-library"); setVisuals(v.data || []); } catch {}
  })(); /* eslint-disable-next-line */ }, [project.project_id]);

  const tset = tsets[0];
  const addTerr = async () => {
    try {
      const r = await api.post(
        `/admin/brand-projects/${project.project_id}/territory-sets/${tset.territory_set_id}/territories`,
        { title: draft.title, rationale: draft.rationale, visuals: draft.visuals.map((v) => ({ visual_id: v })) });
      setTsets([r.data]); setDraft({ title: "", rationale: "", visuals: [] });
      toast.success("Territory added");
    } catch (e) { toast.error(e?.response?.data?.detail || "add failed"); }
  };
  const writeRender = async (terrId) => {
    try {
      const r = await api.post(
        `/admin/brand-projects/${project.project_id}/territory-sets/${tset.territory_set_id}/territories/${terrId}/renders`,
        render);
      setTsets([r.data]); toast.success("Render saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "save failed"); }
  };
  const qa = async (terrId, language, result) => {
    try {
      const r = await api.post(
        `/admin/brand-projects/${project.project_id}/territory-sets/${tset.territory_set_id}/territories/${terrId}/renders/qa`,
        { language, result });
      setTsets([r.data]); toast.success("QA " + result);
    } catch (e) { toast.error(e?.response?.data?.detail || "QA failed"); }
  };
  const markReady = async () => {
    try {
      const r = await api.post(
        `/admin/brand-projects/${project.project_id}/territory-sets/${tset.territory_set_id}/ready`);
      setTsets([r.data]); toast.success("Marked ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "ready failed"); }
  };
  const send = async () => {
    try {
      await api.post(`/admin/brand-projects/${project.project_id}/direction-packages/send`,
        { territory_set_id: tset.territory_set_id, languages: sendLangs });
      toast.success("Direction package sent"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "send failed"); }
  };

  if (!tset) return <p className="text-neutral-500">Project must be in STRATEGY_APPROVED or TERRITORIES_IN_PREPARATION to curate territories.</p>;

  return (
    <div className="max-w-6xl grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="col-span-2 space-y-6" data-testid="territory-workspace">
        <div className="flex items-center gap-3">
          <div className="text-xs text-neutral-500 tracking-widest">TERRITORY SET v{tset.version_number} · {tset.status}</div>
          {tset.stale && <span className="text-red-300 text-xs">STALE</span>}
          <button onClick={() => setPreviewMode(!previewMode)}
            data-testid="toggle-preview"
            className="ml-auto text-xs px-2 py-1 border border-neutral-700 rounded">
            {previewMode ? "Edit" : "Preview"}
          </button>
        </div>

        {!previewMode && tset.status === "preparation" && (
          <div className="p-4 border border-neutral-800 rounded-lg" data-testid="add-territory-form">
            <div className="text-sm font-bold mb-3">Add territory</div>
            <input placeholder="Title (project-specific name)"
              value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2"
              data-testid="territory-title" />
            <textarea placeholder="Short internal rationale"
              value={draft.rationale} onChange={(e) => setDraft({ ...draft, rationale: e.target.value })}
              className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
            <div className="text-xs text-neutral-500 mb-1">Pick visuals ({draft.visuals.length})</div>
            <div className="grid grid-cols-6 gap-1 max-h-48 overflow-y-auto mb-3">
              {visuals.map((v) => (
                <button key={v.visual_id}
                  onClick={() => setDraft({ ...draft,
                    visuals: draft.visuals.includes(v.visual_id)
                      ? draft.visuals.filter((x) => x !== v.visual_id)
                      : [...draft.visuals, v.visual_id] })}
                  className={`aspect-square border rounded overflow-hidden ${
                    draft.visuals.includes(v.visual_id) ? "border-orange-500" : "border-neutral-800"
                  }`} data-testid={`vlib-pick-${v.visual_id}`}>
                  {v.image_url ? <img src={v.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-neutral-900" />}
                </button>
              ))}
            </div>
            <button onClick={addTerr} disabled={!draft.title}
              data-testid="add-territory-btn"
              className="px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm disabled:opacity-30">
              Add territory
            </button>
          </div>
        )}

        <div className="space-y-4" data-testid="territory-list">
          {(tset.territories || []).map((t) => (
            <div key={t.territory_id} className="p-4 border border-neutral-800 rounded-lg"
              data-testid={`territory-${t.code || t.territory_id}`}>
              <div className="flex items-center gap-3">
                <div className="font-black text-lg">{t.code} — {t.title}</div>
              </div>
              <p className="text-xs text-neutral-400 mt-1">{t.rationale}</p>
              <div className="grid grid-cols-6 gap-1 mt-3">
                {(t.visuals || []).map((v, i) => {
                  const vd = visuals.find((x) => x.visual_id === v.visual_id);
                  return <div key={i} className="aspect-square border border-neutral-800 rounded overflow-hidden">
                    {vd?.image_url ? <img src={vd.image_url} alt="" className="w-full h-full object-cover" />
                                   : <div className="w-full h-full bg-neutral-900" />}
                  </div>;
                })}
              </div>
              <div className="mt-3 space-y-2">
                {["en", "es", "pap"].map((lang) => {
                  const r = (t.renders || {})[lang];
                  return (
                    <div key={lang} className="flex items-center gap-2 text-xs">
                      <span className="w-10 uppercase tracking-widest">{lang}</span>
                      <span className={`px-2 py-0.5 rounded border text-[10px] ${
                        r?.qa_status === "approved" ? "border-green-500/40 text-green-300" :
                        r?.qa_status === "pap_qa_blocked" ? "border-red-500/40 text-red-300" :
                        "border-neutral-700 text-neutral-500"
                      }`}>{r?.qa_status || "none"}</span>
                      <span className="text-neutral-500 truncate flex-1">{r?.headline}</span>
                      {tset.status !== "sent" && (
                        <>
                          <button onClick={() => { setRender({ language: lang, headline: r?.headline || "", description: r?.description || "" }); }}
                            className="text-neutral-400 hover:text-neutral-200">edit</button>
                          <button onClick={() => qa(t.territory_id, lang, "approved")}
                            data-testid={`qa-approve-${t.code || t.territory_id}-${lang}`}
                            className="text-green-400">approve</button>
                          {lang === "pap" && (
                            <button onClick={() => qa(t.territory_id, lang, "pap_qa_blocked")}
                              className="text-red-400">block</button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {!previewMode && tset.status === "preparation" && (
          <div className="p-3 border border-neutral-800 rounded-lg" data-testid="write-render-panel">
            <div className="text-xs text-neutral-500 mb-2">WRITE / UPDATE RENDER (pick language, then save on a territory)</div>
            <div className="flex gap-2">
              <select value={render.language} onChange={(e) => setRender({ ...render, language: e.target.value })}
                className="bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm">
                <option value="en">en</option><option value="es">es</option><option value="pap">pap</option>
              </select>
              <input placeholder="Headline" value={render.headline}
                onChange={(e) => setRender({ ...render, headline: e.target.value })}
                className="flex-1 bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" />
            </div>
            <textarea placeholder="Description" value={render.description}
              onChange={(e) => setRender({ ...render, description: e.target.value })}
              className="w-full mt-2 bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" />
            <div className="mt-2 flex gap-2 flex-wrap">
              {(tset.territories || []).map((t) => (
                <button key={t.territory_id} onClick={() => writeRender(t.territory_id)}
                  data-testid={`write-render-${t.code || t.territory_id}`}
                  className="text-xs px-2 py-1 border border-neutral-700 rounded">
                  Save to {t.code}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="p-4 border border-neutral-800 rounded-lg" data-testid="send-panel">
          <div className="text-xs text-neutral-500 tracking-widest mb-2">SEND DIRECTION PACKAGE</div>
          <div className="flex items-center gap-3 flex-wrap">
            {["en", "es", "pap"].map((l) => (
              <label key={l} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={sendLangs.includes(l)}
                  onChange={() => setSendLangs(sendLangs.includes(l)
                    ? sendLangs.filter((x) => x !== l) : [...sendLangs, l])}
                  data-testid={`send-lang-${l}`} />
                <span>{l.toUpperCase()}</span>
              </label>
            ))}
            {tset.status === "preparation" && (
              <button onClick={markReady}
                data-testid="btn-mark-ready"
                className="ml-auto px-3 py-2 border border-neutral-700 rounded text-sm">
                Mark ready for customer
              </button>
            )}
            {tset.status === "ready_for_customer" && (
              <button onClick={send}
                data-testid="btn-send-direction"
                className="ml-auto px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm">
                Send direction package
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3 text-xs" data-testid="territory-strategy-context">
        <div className="text-xs text-neutral-500 tracking-widest">STRATEGY CONTEXT</div>
        <p className="text-neutral-400">
          Current snapshot: {project.current_snapshot_id || "(none)"}<br />
          Intake revision: {project.intake_revision}<br />
          Naming required: {project.naming_required ? "yes" : "no"}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ConceptsTab({ project, refresh }) {
  const api = adminApi();
  const [concepts, setConcepts] = useState([]);
  const [selected, setSelected] = useState([]);
  const load = async () => {
    try {
      const r = await api.get(`/admin/brand-projects/${project.project_id}/concepts`);
      setConcepts(r.data || []);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.project_id]);

  return (
    <div className="max-w-5xl space-y-6">
      {concepts.length === 0 && <p className="text-neutral-500">No concepts submitted yet.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="concept-list">
        {concepts.map((c) => <ConceptCard key={c.concept_id} concept={c} projectId={project.project_id}
          onChanged={load} selected={selected.includes(c.concept_id)}
          onToggle={() => setSelected(selected.includes(c.concept_id)
            ? selected.filter((x) => x !== c.concept_id)
            : [...selected, c.concept_id])} />)}
      </div>
      <div className="flex items-center gap-3 border-t border-neutral-800 pt-4">
        <button
          disabled={!selected.length}
          data-testid="btn-send-concepts"
          onClick={async () => {
            try {
              await api.post(`/admin/brand-projects/${project.project_id}/concepts/send`,
                { concept_ids: selected });
              toast.success("Concepts sent to customer");
              setSelected([]); refresh();
            } catch (e) { toast.error(e?.response?.data?.detail || "send failed"); }
          }}
          className="px-4 py-2 bg-orange-500 text-black font-bold rounded disabled:opacity-30">
          Send {selected.length} to customer
        </button>
        <div className="text-xs text-neutral-500">Only gate_1_passed concepts can be sent.</div>
      </div>
    </div>
  );
}

function ConceptCard({ concept, projectId, onChanged, selected, onToggle }) {
  const api = adminApi();
  const [chk, setChk] = useState(Object.fromEntries(GATE1_CRITERIA.map(([k]) => [k, "PASS"])));
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);
  const overall = overallFrom(chk, GATE1_CRITERIA);

  const submit = async () => {
    try {
      await api.post(`/admin/brand-projects/${projectId}/concepts/${concept.concept_id}/gate-1`,
        { checklist: chk, overall_result: overall, notes });
      toast.success("Gate 1 recorded");
      setOpen(false); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "gate 1 failed"); }
  };

  const statusColor = {
    submitted: "border-amber-500/40 text-amber-300",
    gate_1_passed: "border-green-500/40 text-green-300",
    gate_1_rejected: "border-red-500/40 text-red-300",
    customer_facing: "border-blue-500/40 text-blue-300",
    selected: "border-orange-500/40 text-orange-300",
  }[concept.status] || "border-neutral-700 text-neutral-400";

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden" data-testid={`concept-${concept.concept_id}`}>
      {concept.primary_logo_asset_url && (
        <div className="aspect-[4/3] bg-neutral-900 flex items-center justify-center">
          <img src={concept.primary_logo_asset_url} alt="" className="max-h-[80%] max-w-[80%]" />
        </div>
      )}
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <div className="text-xs text-neutral-500">Round {concept.round_number} · Slot {concept.concept_slot}</div>
          <span className={`ml-auto px-2 py-0.5 rounded border text-[10px] ${statusColor}`}>{concept.status}</span>
          {concept.stale && <span className="text-red-300 text-xs">STALE</span>}
        </div>
        <div className="text-sm text-neutral-300">{concept.color_direction}</div>
        <div className="text-xs text-neutral-500">{concept.typography_direction}</div>
        <div className="text-xs text-neutral-500">Originality: {concept.originality_source || "—"}</div>
        {concept.status === "submitted" && !concept.stale && (
          <button onClick={() => setOpen(!open)}
            data-testid={`gate1-open-${concept.concept_id}`}
            className="text-xs px-2 py-1 border border-neutral-700 rounded">
            Gate 1 review
          </button>
        )}
        {concept.status === "gate_1_passed" && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={selected} onChange={onToggle}
              data-testid={`concept-select-${concept.concept_id}`} />
            <span>Include in customer send</span>
          </label>
        )}
        {open && (
          <div className="pt-3 border-t border-neutral-800 space-y-2" data-testid={`gate1-panel-${concept.concept_id}`}>
            {GATE1_CRITERIA.map(([k, lbl]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="flex-1 text-xs">{lbl}</span>
                {["PASS", "NEEDS_WORK"].map((v) => (
                  <button key={v} onClick={() => setChk({ ...chk, [k]: v })}
                    data-testid={`g1-${k}-${v}`}
                    className={`px-2 py-1 text-[10px] rounded border ${
                      chk[k] === v
                        ? v === "PASS" ? "border-green-500 text-green-300 bg-green-500/10"
                                        : "border-red-500 text-red-300 bg-red-500/10"
                        : "border-neutral-700 text-neutral-500"
                    }`}>{v}</button>
                ))}
              </div>
            ))}
            <textarea placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-neutral-900 border border-neutral-800 rounded p-2 text-xs" />
            <div className="text-xs text-neutral-500">
              Overall will record as: <span className={overall === "PASS" ? "text-green-300" : "text-red-300"}>{overall}</span>
            </div>
            <button onClick={submit}
              data-testid={`gate1-submit-${concept.concept_id}`}
              className="w-full px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm">
              Record Gate 1
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FeedbackTab({ project, refresh }) {
  const api = adminApi();
  const [reworks, setReworks] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [issues, setIssues] = useState([]);
  const [draft, setDraft] = useState({ customer_note: "", neither_feels_right: false, concept_id: "", direction_package_id: "" });

  const load = async () => {
    try { const d = await api.get(`/admin/brand-projects/${project.project_id}/concept-decisions`); setDecisions(d.data || []); } catch {}
    try { const i = await api.get(`/admin/brand-projects/${project.project_id}/brief-issues`); setIssues(i.data || []); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.project_id]);

  const create = async () => {
    try {
      const r = await api.post(`/admin/brand-projects/${project.project_id}/rework-requests`, draft);
      setReworks([...reworks, r.data]);
      setDraft({ customer_note: "", neither_feels_right: false, concept_id: "", direction_package_id: "" });
      toast.success("Rework request recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "create failed"); }
  };
  const classify = async (id, classification) => {
    try {
      const r = await api.post(`/admin/brand-projects/${project.project_id}/rework-requests/${id}/classify`,
        { classification });
      setReworks(reworks.map((x) => x.rework_request_id === id ? r.data : x));
      toast.success("Classified: " + classification);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "classify failed"); }
  };
  const classifyDecision = async (id, classification) => {
    try {
      await api.post(
        `/admin/brand-projects/${project.project_id}/concept-decisions/${id}/classify`,
        { classification });
      toast.success("Decision classified: " + classification);
      load(); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "classify failed"); }
  };
  const resolveIssue = async (id) => {
    try {
      await api.post(
        `/admin/brand-projects/${project.project_id}/brief-issues/${id}/resolve`,
        { resolution_note: "" });
      toast.success("Issue resolved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "resolve failed"); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      {decisions.length > 0 && (
        <div className="p-4 border border-neutral-800 rounded-lg" data-testid="customer-decisions-panel">
          <div className="text-xs text-neutral-500 tracking-widest mb-2">CUSTOMER CONCEPT DECISIONS</div>
          {decisions.map((d) => (
            <div key={d.concept_decision_id} className="p-3 border border-neutral-900 rounded mb-2 text-sm"
              data-testid={`decision-${d.concept_decision_id}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-neutral-500">By {d.approver_name}</span>
                <span className={`ml-auto px-2 py-0.5 rounded border text-[10px] ${
                  d.status === "reviewer_classified" ? "border-green-500/40 text-green-300" :
                                                        "border-amber-500/40 text-amber-300"
                }`}>{d.status}</span>
              </div>
              <div className="mt-1">
                {d.neither_feels_right
                  ? <span className="text-red-300">Neither felt right · {d.neither_reason}</span>
                  : <span>Picked concept <span className="font-mono text-xs">{d.concept_id}</span></span>}
              </div>
              {d.like_choice && <div className="text-xs text-neutral-400 mt-1">Liked: {d.like_choice}</div>}
              {d.adjust_note && <div className="text-xs text-neutral-400 mt-1">Note: {d.adjust_note}</div>}
              {d.status === "submitted" && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => classifyDecision(d.concept_decision_id, "refinement")}
                    data-testid={`classify-decision-ref-${d.concept_decision_id}`}
                    className="text-xs px-2 py-1 border border-neutral-700 rounded">Refinement</button>
                  <button onClick={() => classifyDecision(d.concept_decision_id, "direction_change")}
                    data-testid={`classify-decision-dc-${d.concept_decision_id}`}
                    className="text-xs px-2 py-1 border border-red-500/40 text-red-300 rounded">Direction change</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {issues.length > 0 && (
        <div className="p-4 border border-neutral-800 rounded-lg" data-testid="brief-issues-panel">
          <div className="text-xs text-neutral-500 tracking-widest mb-2">DESIGNER BRIEF ISSUES</div>
          {issues.map((i) => (
            <div key={i.brief_issue_id} className="p-3 border border-neutral-900 rounded mb-2 text-sm"
              data-testid={`admin-issue-${i.brief_issue_id}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-neutral-500">{i.category}</span>
                <span className={`ml-auto px-2 py-0.5 rounded border text-[10px] ${
                  i.status === "resolved" ? "border-green-500/40 text-green-300" :
                                             "border-amber-500/40 text-amber-300"
                }`}>{i.status}</span>
              </div>
              <div className="mt-1">{i.note || "(no note)"}</div>
              {i.status === "submitted" && (
                <button onClick={() => resolveIssue(i.brief_issue_id)}
                  data-testid={`resolve-issue-${i.brief_issue_id}`}
                  className="mt-2 text-xs px-2 py-1 border border-green-500/40 text-green-300 rounded">
                  Mark resolved
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="p-4 border border-neutral-800 rounded-lg" data-testid="feedback-create">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">RECORD CUSTOMER FEEDBACK</div>
        <input placeholder="Direction package ID (optional)"
          value={draft.direction_package_id}
          onChange={(e) => setDraft({ ...draft, direction_package_id: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <input placeholder="Concept ID (optional)"
          value={draft.concept_id}
          onChange={(e) => setDraft({ ...draft, concept_id: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <textarea placeholder="Customer note (your words)" value={draft.customer_note}
          onChange={(e) => setDraft({ ...draft, customer_note: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={draft.neither_feels_right}
            onChange={(e) => setDraft({ ...draft, neither_feels_right: e.target.checked })} />
          <span>Neither felt right</span>
        </label>
        <button onClick={create} data-testid="feedback-create-btn"
          className="px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm">Create</button>
      </div>
      <div className="space-y-3" data-testid="feedback-list">
        {reworks.map((r) => (
          <div key={r.rework_request_id} className="p-3 border border-neutral-800 rounded-lg"
            data-testid={`feedback-${r.rework_request_id}`}>
            <div className="text-xs text-neutral-500">{r.rework_request_id}</div>
            <p className="text-sm mt-1">{r.customer_note || "(no note)"}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs">Classification:</span>
              <span className="text-xs text-orange-300">{r.classification || "unclassified"}</span>
              {!r.classification && (
                <div className="ml-auto flex gap-2">
                  <button onClick={() => classify(r.rework_request_id, "refinement")}
                    data-testid={`classify-ref-${r.rework_request_id}`}
                    className="text-xs px-2 py-1 border border-neutral-700 rounded">Refinement</button>
                  <button onClick={() => classify(r.rework_request_id, "direction_change")}
                    data-testid={`classify-dc-${r.rework_request_id}`}
                    className="text-xs px-2 py-1 border border-red-500/40 text-red-300 rounded">Direction change</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function IdentityTab({ project, refresh }) {
  const api = adminApi();
  const [handoffs, setHandoffs] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [draft, setDraft] = useState({ concept_id: "", primary_logo_asset_url: "", source_licensing_notes: "" });

  useEffect(() => { (async () => {
    try {
      const c = await api.get(`/admin/brand-projects/${project.project_id}/concepts`);
      setConcepts((c.data || []).filter((x) => ["customer_facing", "selected"].includes(x.status)));
    } catch {}
  })(); /* eslint-disable-next-line */ }, [project.project_id]);

  const create = async () => {
    try {
      const r = await api.post(`/admin/brand-projects/${project.project_id}/identity-handoffs`, draft);
      setHandoffs([...handoffs, r.data]); toast.success("Handoff drafted");
    } catch (e) { toast.error(e?.response?.data?.detail || "create failed"); }
  };
  const approve = async (id) => {
    try {
      await api.post(`/admin/brand-projects/${project.project_id}/identity-handoffs/${id}/approve`);
      toast.success("Approved"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "approve failed"); }
  };
  return (
    <div className="max-w-3xl space-y-6">
      <div className="p-4 border border-neutral-800 rounded-lg" data-testid="identity-create">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">NEW IDENTITY HANDOFF</div>
        <select value={draft.concept_id} onChange={(e) => setDraft({ ...draft, concept_id: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2"
          data-testid="identity-concept-select">
          <option value="">Select customer-facing concept</option>
          {concepts.map((c) => <option key={c.concept_id} value={c.concept_id}>{c.concept_id} ({c.status})</option>)}
        </select>
        <input placeholder="Primary logo asset URL"
          value={draft.primary_logo_asset_url}
          onChange={(e) => setDraft({ ...draft, primary_logo_asset_url: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2"
          data-testid="identity-primary-url" />
        <textarea placeholder="Source / licensing notes"
          value={draft.source_licensing_notes}
          onChange={(e) => setDraft({ ...draft, source_licensing_notes: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <button onClick={create} disabled={!draft.concept_id}
          data-testid="identity-create-btn"
          className="px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm disabled:opacity-30">
          Draft handoff
        </button>
      </div>
      <div className="space-y-3" data-testid="identity-list">
        {handoffs.map((h) => (
          <div key={h.identity_handoff_id} className="p-3 border border-neutral-800 rounded-lg">
            <div className="flex items-center gap-2 text-xs">
              <span>v{h.version_number}</span>
              <span className="text-neutral-500">{h.status}</span>
              {h.status === "preparation" && (
                <button onClick={() => approve(h.identity_handoff_id)}
                  data-testid={`identity-approve-${h.identity_handoff_id}`}
                  className="ml-auto px-2 py-1 bg-orange-500 text-black font-bold rounded">
                  Approve
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function BlueprintTab({ project }) {
  const api = adminApi();
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState({ language: "en", identity_handoff_id: "", sections: { intro: "" }, rendered_asset_url: "" });
  const [handoffs, setHandoffs] = useState([]);
  useEffect(() => { (async () => {
    try {
      const h = await api.get(`/admin/brand-projects/${project.project_id}`).catch(() => null);
      void h;
    } catch {}
  })(); /* eslint-disable-next-line */ }, [project.project_id]);
  const create = async () => {
    try {
      const r = await api.post(`/admin/brand-projects/${project.project_id}/blueprints`, draft);
      setList([...list, r.data]); toast.success("Blueprint ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "blueprint failed"); }
  };
  return (
    <div className="max-w-3xl space-y-6">
      <div className="p-4 border border-neutral-800 rounded-lg" data-testid="blueprint-create">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">BLUEPRINT (EN + ES; no PAP in V1)</div>
        <select value={draft.language} onChange={(e) => setDraft({ ...draft, language: e.target.value })}
          data-testid="bp-lang"
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2">
          <option value="en">EN</option><option value="es">ES</option>
        </select>
        <input placeholder="Identity Handoff ID (approved)"
          value={draft.identity_handoff_id}
          onChange={(e) => setDraft({ ...draft, identity_handoff_id: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2"
          data-testid="bp-handoff-id" />
        <input placeholder="Rendered asset URL (PDF)"
          value={draft.rendered_asset_url}
          onChange={(e) => setDraft({ ...draft, rendered_asset_url: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <textarea placeholder="Section: intro"
          value={draft.sections.intro}
          onChange={(e) => setDraft({ ...draft, sections: { intro: e.target.value } })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <button onClick={create} disabled={!draft.identity_handoff_id}
          data-testid="bp-create-btn"
          className="px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm disabled:opacity-30">
          Create blueprint
        </button>
      </div>
      <div className="space-y-2" data-testid="blueprint-list">
        {list.map((b) => (
          <div key={b.blueprint_id} className="p-3 border border-neutral-800 rounded-lg text-xs">
            {b.language.toUpperCase()} · v{b.version_number} · {b.status}
            {b.stale && <span className="ml-2 text-red-300">STALE</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PackageTab({ project, refresh }) {
  const api = adminApi();
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState({ identity_handoff_id: "", blueprint_ids: "", files: "" });
  const create = async () => {
    try {
      const r = await api.post(`/admin/brand-projects/${project.project_id}/package-manifests`, {
        identity_handoff_id: draft.identity_handoff_id,
        blueprint_ids: draft.blueprint_ids.split(",").map((x) => x.trim()).filter(Boolean),
        files: draft.files.split("\n").filter(Boolean).map((line) => {
          const [label, url, sha, bytes] = line.split("|");
          return { label: label || "file", file_url: url || "", sha256: sha || "", bytes: parseInt(bytes) || 0 };
        }),
      });
      setList([...list, r.data]); toast.success("Manifest created");
    } catch (e) { toast.error(e?.response?.data?.detail || "manifest failed"); }
  };
  const gate2 = async (manifest_id, result) => {
    const checklist = Object.fromEntries(GATE2_CRITERIA.map(([k]) => [k, result]));
    try {
      const r = await api.post(`/admin/brand-projects/${project.project_id}/package-manifests/${manifest_id}/gate-2`,
        { checklist, overall_result: result });
      toast.success("Gate 2: " + result);
      setList(list.map((x) => x.package_manifest_id === manifest_id ? r.data.manifest : x));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "gate 2 failed"); }
  };
  const deliver = async (manifest_id) => {
    try {
      await api.post(`/admin/brand-projects/${project.project_id}/deliveries`,
        { package_manifest_id: manifest_id });
      toast.success("Delivered"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "deliver failed"); }
  };
  const complete = async () => {
    try {
      await api.post(`/admin/brand-projects/${project.project_id}/complete`, { note: "" });
      toast.success("Project complete"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "complete failed"); }
  };
  return (
    <div className="max-w-3xl space-y-6">
      <div className="p-4 border border-neutral-800 rounded-lg" data-testid="package-create">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">NEW PACKAGE MANIFEST</div>
        <input placeholder="Identity Handoff ID (approved)" value={draft.identity_handoff_id}
          onChange={(e) => setDraft({ ...draft, identity_handoff_id: e.target.value })}
          data-testid="pkg-handoff-id"
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <input placeholder="Blueprint IDs (comma-separated)" value={draft.blueprint_ids}
          onChange={(e) => setDraft({ ...draft, blueprint_ids: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <textarea placeholder="Files (one per line: label|url|sha256|bytes)" value={draft.files}
          onChange={(e) => setDraft({ ...draft, files: e.target.value })}
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2 font-mono text-xs" />
        <button onClick={create} data-testid="pkg-create-btn"
          disabled={!draft.identity_handoff_id}
          className="px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm disabled:opacity-30">
          Create manifest
        </button>
      </div>
      <div className="space-y-3" data-testid="package-list">
        {list.map((m) => (
          <div key={m.package_manifest_id} className="p-3 border border-neutral-800 rounded-lg">
            <div className="text-xs text-neutral-500">v{m.version_number} · {m.status}</div>
            <div className="text-[10px] text-neutral-500 font-mono truncate">hash: {m.manifest_hash}</div>
            {m.gate_2_passed_hash && m.gate_2_passed_hash !== m.manifest_hash && (
              <div className="text-xs text-red-300 mt-1" data-testid={`gate2-invalid-${m.package_manifest_id}`}>
                Previous Gate 2 PASS invalidated (content changed).
              </div>
            )}
            <div className="mt-2 flex gap-2 flex-wrap">
              <button onClick={() => gate2(m.package_manifest_id, "PASS")}
                data-testid={`gate2-pass-${m.package_manifest_id}`}
                className="px-2 py-1 text-xs border border-green-500/40 text-green-300 rounded">Gate 2 PASS</button>
              <button onClick={() => gate2(m.package_manifest_id, "NEEDS_WORK")}
                className="px-2 py-1 text-xs border border-red-500/40 text-red-300 rounded">Gate 2 NEEDS_WORK</button>
              {m.status === "gate_2_passed" && (
                <button onClick={() => deliver(m.package_manifest_id)}
                  data-testid={`deliver-${m.package_manifest_id}`}
                  className="px-2 py-1 text-xs bg-orange-500 text-black font-bold rounded">Deliver</button>
              )}
            </div>
          </div>
        ))}
      </div>
      {project.project_status === "DELIVERED" && (
        <button onClick={complete} data-testid="btn-complete"
          className="px-4 py-2 bg-orange-500 text-black font-bold rounded">
          Mark project complete
        </button>
      )}
    </div>
  );
}
